/** @format */

import {useMemo} from 'react';

import {format} from 'date-fns';

import {TanStackTableData} from 'components/Table/core/types';

import {useGetCampaignStatisticsDataQuery} from '../graphql/__generated__';
import {
  aggregateCampaignStatistics,
  CampaignStatisticsTotals,
} from '../helpers/aggregateCampaignStatistics';

interface UseCampaignStatisticsDataOptions {
  companyId: string;
  startDate: Date;
  endDate: Date;
  mediaplanType?: string | null;
  search?: string;
}

interface UseCampaignStatisticsDataReturn {
  tableData: TanStackTableData[];
  totals: CampaignStatisticsTotals | null;
  companyName: string;
  companyMediaplanType: string;
  companyNotFound: boolean;
  loading: boolean;
  error: ReturnType<typeof useGetCampaignStatisticsDataQuery>['error'];
}

const useCampaignStatisticsData = ({
  companyId,
  startDate,
  endDate,
  mediaplanType,
  search,
}: UseCampaignStatisticsDataOptions): UseCampaignStatisticsDataReturn => {
  const startDateStr = format(startDate, 'yyyy-MM-dd');
  const endDateStr = format(endDate, 'yyyy-MM-dd');

  const {data, loading, error} = useGetCampaignStatisticsDataQuery({
    variables: {
      companyId,
      startDate: startDateStr,
      endDate: endDateStr,
    },
  });

  const company = data?.currentUser?.company;
  const stats = company?.campaignMediaplanStats;
  const companyName = company?.name ?? '';
  const companyMediaplanType = company?.mediaplanType ?? '';

  const {tableData, totals} = useMemo(() => {
    if (!stats) {
      return {tableData: [] as TanStackTableData[], totals: null};
    }
    return aggregateCampaignStatistics({
      data: stats,
      mediaplanType,
      search,
    });
  }, [stats, mediaplanType, search]);

  const companyNotFound = !loading && !error && !!data && !company;

  return {
    tableData,
    totals,
    companyName,
    companyMediaplanType,
    companyNotFound,
    loading,
    error,
  };
};

export {useCampaignStatisticsData};
